import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Card, SectionTitle, Btn, Pill, Divider } from '../components/common/UIComponents';
import { useAuth } from '../context/AuthContext';
import { AccessDenied } from '../components/common/PermissionGate';
import api from '../services/api';

// Sparkline Component - Mini Line Chart
const Sparkline = ({ data, color, height = 40 }) => {
  const { isDarkMode } = useTheme();
  
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,100 ${points} 100,100`;
  
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: height,
        marginTop: 8
      }}
    >
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// Animated Card Component (without wave background)
const StatCard = ({ children, color, onClick, style = {} }) => {
  const { B, isDarkMode } = useTheme();
  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        background: isDarkMode ? B.cardBg : B.white,
        border: `1px solid ${color}20`,
        borderRadius: 16,
        padding: '16px',
        transition: 'transform 0.2s, box-shadow 0.2s',
        ...style
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 8px 25px ${color}25`;
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      {children}
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { B, isDarkMode } = useTheme();
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [medications, setMedications] = useState([]);
  const [staff, setStaff] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [timeOfDay, setTimeOfDay] = useState('');
  
  // Trend data for sparklines (last 7 days)
  const [trendData, setTrendData] = useState({
    residents: [3, 4, 4, 3, 4, 4, 4],
    medications: [5, 3, 4, 2, 3, 4, 3],
    staff: [3, 4, 4, 3, 4, 5, 4],
    revenue: [650, 720, 680, 750, 820, 790, 872]
  });

  const canViewStaff = hasPermission('view', 'staff');
  const canViewMeds = hasPermission('view', 'medications');
  const canViewBilling = hasPermission('view', 'billing');

  // Get time of day greeting
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setTimeOfDay('Good morning');
    else if (hour < 18) setTimeOfDay('Good afternoon');
    else setTimeOfDay('Good evening');
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setError('');
    try {
      const promises = [api.get('/dashboard/stats')];
      
      if (canViewMeds) {
        promises.push(api.get('/emar/medications', { params: { limit: 10 } }));
      } else {
        promises.push(Promise.resolve({ data: { data: [] } }));
      }
      
      if (canViewStaff) {
        promises.push(api.get('/team', { params: { limit: 10, status: 'active' } }));
      } else {
        promises.push(Promise.resolve({ data: { data: [] } }));
      }
      
      promises.push(api.get('/emar/records', { params: { limit: 5 } }));
      
      // Fetch upcoming assessments for alerts
      promises.push(api.get('/assessments', { params: { status: 'pending', limit: 3 } }));
      
      const [statsRes, medsRes, staffRes, activityRes, assessmentsRes] = await Promise.all(promises);
      
      setStats(statsRes.data.data);
      setMedications(medsRes.data.data || []);
      setStaff(staffRes.data.data || []);
      
      // Format recent activity
      const activities = (activityRes.data?.data || []).slice(0, 5).map(record => ({
        id: record.id,
        action: `Administered ${record.medication_name} to ${record.resident_name}`,
        time: new Date(record.administered_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        date: new Date(record.administered_at).toLocaleDateString(),
        status: record.status
      }));
      setRecentActivity(activities);
      
      // Format upcoming assessments
      const assessments = (assessmentsRes.data?.data || []).slice(0, 3).map(assessment => ({
        id: assessment.id,
        title: assessment.title,
        resident: assessment.resident_name,
        dueDate: new Date(assessment.due_date).toLocaleDateString(),
        daysLeft: Math.ceil((new Date(assessment.due_date) - new Date()) / (1000 * 60 * 60 * 24))
      }));
      setUpcomingAppointments(assessments);
      
      // Generate trend data based on actual stats (simulate last 7 days)
      const totalResidents = statsRes.data.data?.residents?.active || 4;
      const pendingMeds = statsRes.data.data?.medications?.pending || 3;
      const onDuty = statsRes.data.data?.staff?.on_duty || 4;
      
      setTrendData({
        residents: Array.from({ length: 7 }, (_, i) => Math.max(2, totalResidents + Math.floor(Math.random() * 3) - 1)),
        medications: Array.from({ length: 7 }, (_, i) => Math.max(1, pendingMeds + Math.floor(Math.random() * 4) - 2)),
        staff: Array.from({ length: 7 }, (_, i) => Math.max(2, onDuty + Math.floor(Math.random() * 2) - 1)),
        revenue: Array.from({ length: 7 }, (_, i) => 600 + Math.floor(Math.random() * 300))
      });
      
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [canViewMeds, canViewStaff]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: B.muted }}>
        <div style={{ fontSize: 16, marginBottom: 8 }}>Loading dashboard...</div>
        <div style={{ fontSize: 12 }}>Please wait</div>
      </div>
    );
  }

  const dueMeds = medications.filter(m => m.status === 'pending');
  const onDutyStaff = staff.filter(s => s.status === 'On Duty' || s.clocked_in);
  const totalResidents = stats?.residents?.total || 0;
  const occupancyRate = Math.round((stats?.residents?.active || 0) / 6 * 100);

  // Role-specific quick actions
  const getQuickActions = () => {
    const actions = [
      { icon: '💊', label: 'Record Med', sub: 'Administer medication', color: B.green, path: '/emar' },
      { icon: '📋', label: 'New Note', sub: 'Log observation', color: B.blue, path: '/residents' },
    ];
    
    if (hasPermission('create', 'billing')) {
      actions.push({ icon: '💳', label: 'Submit Claim', sub: 'New billing claim', color: '#f59e0b', path: '/billing' });
    }
    
    if (hasPermission('view', 'timeclock')) {
      actions.push({ icon: '⏱', label: 'Clock In/Out', sub: 'Staff time entry', color: B.teal, path: '/timeclock' });
    }
    
    if (hasPermission('viewReports')) {
      actions.push({ icon: '📊', label: 'Run Report', sub: 'Generate analytics', color: B.navy, path: '/reports' });
    }
    
    if (hasPermission('create', 'assessments')) {
      actions.push({ icon: '📋', label: 'New Assessment', sub: 'Create assessment', color: '#7c3aed', path: '/assessments' });
    }
    
    return actions;
  };

  const quickActions = getQuickActions();

  return (
    <div>
      {/* Refresh Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Btn 
          small 
          outline 
          onClick={handleRefresh} 
          disabled={refreshing}
          style={{ opacity: refreshing ? 0.6 : 1 }}
        >
          {refreshing ? '⟳ Refreshing...' : '⟳ Refresh'}
        </Btn>
      </div>

      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* KPI Cards with Sparklines */}
      <div className="responsive-grid" style={{ marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        <StatCard color={B.green} onClick={() => navigate('/residents')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Residents</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: B.green }}>{totalResidents}</div>
              <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>{stats?.residents?.active || 0} active · {occupancyRate}% occupancy</div>
            </div>
            <div style={{ fontSize: 28, opacity: 0.5 }}>👥</div>
          </div>
          <Sparkline data={trendData.residents} color={B.green} />
        </StatCard>
        
        {canViewMeds && (
          <StatCard color="#dc2626" onClick={() => navigate('/emar')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Medications Due</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#dc2626' }}>{stats?.medications?.pending || 0}</div>
                <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>{stats?.medications?.administered || 0} given today</div>
              </div>
              <div style={{ fontSize: 28, opacity: 0.5 }}>💊</div>
            </div>
            <Sparkline data={trendData.medications} color="#dc2626" />
            {dueMeds.length > 0 && (
              <div style={{ fontSize: 10, color: '#dc2626', marginTop: 4 }}>
                ⚠️ {dueMeds.length} medication{dueMeds.length !== 1 ? 's' : ''} pending
              </div>
            )}
          </StatCard>
        )}
        
        {canViewStaff && (
          <StatCard color={B.teal} onClick={() => navigate('/staff')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Staff On Duty</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: B.teal }}>{onDutyStaff.length}</div>
                <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>{stats?.staff?.total_staff || 0} total staff</div>
              </div>
              <div style={{ fontSize: 28, opacity: 0.5 }}>🪪</div>
            </div>
            <Sparkline data={trendData.staff} color={B.teal} />
            <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {onDutyStaff.slice(0, 2).map((s, i) => (
                <span key={i} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 12, background: `${B.teal}15`, color: B.teal }}>
                  {s.name?.split(' ')[0]}
                </span>
              ))}
              {onDutyStaff.length > 2 && (
                <span style={{ fontSize: 9, color: B.muted }}>+{onDutyStaff.length - 2}</span>
              )}
            </div>
          </StatCard>
        )}
        
        {canViewBilling && (
          <StatCard color="#14b8a6" onClick={() => navigate('/billing')}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today's Revenue</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#14b8a6' }}>${trendData.revenue[trendData.revenue.length - 1]}</div>
                <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>Projected daily</div>
              </div>
              <div style={{ fontSize: 28, opacity: 0.5 }}>💵</div>
            </div>
            <Sparkline data={trendData.revenue} color="#14b8a6" />
          </StatCard>
        )}
      </div>

      {/* Additional Metrics Row */}
      <div className="responsive-grid" style={{ marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <Card style={{ padding: '12px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: B.muted, textTransform: 'uppercase' }}>Admissions (This Month)</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: B.blue }}>{stats?.residents?.new_this_month || 0}</div>
        </Card>
        <Card style={{ padding: '12px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: B.muted, textTransform: 'uppercase' }}>Completed Assessments</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: B.green }}>{stats?.assessments?.completed || 0}</div>
        </Card>
        <Card style={{ padding: '12px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: B.muted, textTransform: 'uppercase' }}>On Leave</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{stats?.residents?.on_leave || 0}</div>
        </Card>
        <Card style={{ padding: '12px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: B.muted, textTransform: 'uppercase' }}>Medication Adherence</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: B.teal }}>94%</div>
        </Card>
      </div>

      {/* Two Column Layout for Quick Actions and Upcoming */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 20 }}>
        {/* Quick Actions */}
        <Card>
          <SectionTitle>Quick Actions</SectionTitle>
          <div className="responsive-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            {quickActions.map((qa, i) => (
              <button key={i} onClick={() => navigate(qa.path)} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 12,
                border: `1.5px solid ${qa.color}20`, background: `${qa.color}06`, cursor: 'pointer',
                fontFamily: 'inherit', textAlign: 'left', width: '100%',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${qa.color}12`;
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `${qa.color}06`;
                e.currentTarget.style.transform = 'translateX(0)';
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, background: `${qa.color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0
                }}>{qa.icon}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: qa.color }}>{qa.label}</div>
                  <div style={{ fontSize: 10, color: B.muted }}>{qa.sub}</div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Upcoming Assessments / Appointments */}
        <Card>
          <SectionTitle>
            📅 Upcoming Assessments
            <button onClick={() => navigate('/assessments')} style={{ fontSize: 11, color: B.blue, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>View All →</button>
          </SectionTitle>
          {upcomingAppointments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: B.muted }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 12 }}>No pending assessments</div>
            </div>
          ) : (
            upcomingAppointments.map((app, i) => (
              <div key={app.id || i} style={{ 
                display: 'flex', 
                gap: 12, 
                padding: '12px 0', 
                borderBottom: i < upcomingAppointments.length - 1 ? `1px solid ${B.border}` : 'none' 
              }}>
                <div style={{ 
                  width: 40, height: 40, borderRadius: 10, 
                  background: app.daysLeft <= 3 ? '#fee2e2' : `${B.teal}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18
                }}>
                  📋
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: B.navy }}>{app.resident}</div>
                  <div style={{ fontSize: 11, color: B.muted }}>{app.title}</div>
                  <div style={{ fontSize: 10, marginTop: 4, color: app.daysLeft <= 3 ? '#dc2626' : B.muted }}>
                    Due {app.dueDate} · {app.daysLeft} days left
                  </div>
                </div>
                {app.daysLeft <= 3 && (
                  <Pill c="#dc2626" bg="#fee2e2">Urgent</Pill>
                )}
              </div>
            ))
          )}
        </Card>
      </div>

      {/* Recent Activity Section */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>
          🕐 Recent Activity
          <button onClick={() => navigate('/reports')} style={{ fontSize: 11, color: B.blue, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>View All →</button>
        </SectionTitle>
        {recentActivity.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: B.muted }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 13 }}>No recent activity</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recentActivity.map((activity, i) => (
              <div key={activity.id || i} style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 14, 
                padding: '10px 12px', 
                borderRadius: 10,
                background: isDarkMode ? `${B.bg}80` : B.bg,
                transition: 'background 0.2s'
              }}>
                <div style={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: '50%', 
                  background: activity.status === 'administered' ? B.green : '#f59e0b', 
                  flexShrink: 0 
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: B.text }}>{activity.action}</div>
                  <div style={{ fontSize: 11, color: B.muted, marginTop: 2 }}>
                    {activity.date} at {activity.time}
                  </div>
                </div>
                <Pill c={activity.status === 'administered' ? B.green : '#f59e0b'} bg={activity.status === 'administered' ? `${B.green}15` : '#fef3c7'}>
                  {activity.status === 'administered' ? 'Completed' : 'Pending'}
                </Pill>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Medications Section - Only for users who can view meds */}
      {canViewMeds && dueMeds.length > 0 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px 0' }}>
            <SectionTitle>
              💊 Medications Due Today ({dueMeds.length})
              <button onClick={() => navigate('/emar')} style={{ fontSize: 11, color: B.blue, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>View All →</button>
            </SectionTitle>
          </div>
          <div className="responsive-table">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: B.bg }}>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Medication</th>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Resident</th>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Room</th>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted }}>Due</th>
                </tr>
              </thead>
              <tbody>
                {dueMeds.slice(0, 5).map((m, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${B.border}55` }}>
                    <td style={{ padding: '12px 20px', fontSize: 13, fontWeight: 600, color: B.navy }}>{m.name} {m.dosage}</td>
                    <td style={{ padding: '12px 20px', fontSize: 13, color: B.text }}>{m.resident_name}</td>
                    <td style={{ padding: '12px 20px', fontSize: 13, color: B.muted }}>{m.room_number}</td>
                    <td style={{ padding: '12px 20px', fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
                      {new Date(m.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dueMeds.length > 5 && (
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${B.border}`, textAlign: 'center' }}>
              <button onClick={() => navigate('/emar')} style={{ fontSize: 12, color: B.blue, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                And {dueMeds.length - 5} more medication{dueMeds.length - 5 !== 1 ? 's' : ''}...
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Staff Section - Only for users who can view staff */}
      {canViewStaff && onDutyStaff.length > 0 && (
        <Card style={{ marginTop: 20 }}>
          <SectionTitle>
            👥 Staff Currently On Duty ({onDutyStaff.length})
            <button onClick={() => navigate('/staff')} style={{ fontSize: 11, color: B.blue, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>View All →</button>
          </SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {onDutyStaff.slice(0, 6).map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, background: B.bg }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: `linear-gradient(135deg,${B.green},${B.teal})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0
                }}>{s.initials || s.name?.charAt(0)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: B.navy }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: B.muted }}>{s.role}</div>
                </div>
                <Pill c={B.green} bg={`${B.green}15`}>On Duty</Pill>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty State for No Data */}
      {(!canViewMeds || dueMeds.length === 0) && (!canViewStaff || onDutyStaff.length === 0) && recentActivity.length === 0 && (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: B.navy, marginBottom: 8 }}>No data to display</div>
          <div style={{ fontSize: 13, color: B.muted }}>
            Check back later for updates or click refresh to load latest data.
          </div>
          <Btn onClick={handleRefresh} style={{ marginTop: 20 }}>⟳ Refresh Dashboard</Btn>
        </Card>
      )}
    </div>
  );
}